import React, { useEffect, useState } from 'react';

import { useReplayContext } from './ReplayContext';
import { useViewMode, ViewMode } from '../../components/ViewModeProvider';

interface ReplayOverlay {
  image: string;
  replayIdentifier: string;
}

export const useListenForReplay = (): [
  string | null,
  React.Dispatch<React.SetStateAction<string | null>>,
] => {
  const {
    replayOverlay,
    setReplayOverlay,
    replayIdentifier,
    setReplayIdentifier,
    replayRequestDatetime,
    setReplayRequestDatetime,
    hasLoaded,
    tracker,
  } = useReplayContext();
  const { viewMode } = useViewMode();
  const [isFirstReplayAfterLoad, setIsFirstReplayAfterLoad] = useState(true);

  useEffect(() => {
    if (!hasLoaded) {
      setIsFirstReplayAfterLoad(true);
    }
  }, [hasLoaded]);

  const [timeDrift, setTimeDrift] = useState(0);

  useEffect(() => {
    const handleReplayOverlay = async (event: Event) => {
      if (viewMode !== ViewMode.Replay) {
        return;
      }
      const overlay = event as CustomEvent<ReplayOverlay>;
      // Ignore unexpected images from replay. We only one want the replay with the current replayIdentifier
      if (overlay.detail.replayIdentifier !== replayIdentifier) {
        return;
      }

      // Guard clause that makes sure we have a replayRequestDatetime
      if (!replayRequestDatetime) {
        return;
      }
      const now = Date.now();
      const timeDifference = now - replayRequestDatetime.getTime();
      // If the time difference is less than 1000ms, wait for the remaining time so we have a tick rate of 1 second
      if (timeDifference < 1000) {
        const remainingTime = 1000 - timeDifference;
        const reducedDrift = Math.min(timeDrift, remainingTime, 400);
        const waitTime = remainingTime - reducedDrift;

        setTimeout(() => {
          setReplayOverlay(overlay.detail.image);
          setReplayIdentifier(null);
          setReplayRequestDatetime(null);

          // Decrease timeDrift by the reduced amount, ensuring it doesn't go below zero
          setTimeDrift((prevTimeDrift) =>
            Math.max(prevTimeDrift - reducedDrift, 0),
          );
        }, waitTime);
      } else {
        // We do not want to get count drift on the first replay as it's loading data, and
        //  the user is aware of that
        if (!isFirstReplayAfterLoad) {
          setTimeDrift(
            (prevTimeDrift) => prevTimeDrift + timeDifference - 1000,
          );
        }
        // If it's already more than 1000ms, set them immediately
        setReplayOverlay(overlay.detail.image);
        setReplayIdentifier(null);
        setReplayRequestDatetime(null);
      }
      setIsFirstReplayAfterLoad(false);
      tracker.stopTransaction({
        isFirstReplayAfterLoad: isFirstReplayAfterLoad,
        timeDriftBefore: timeDrift,
        timeDriftAfter: !isFirstReplayAfterLoad
          ? timeDrift + timeDifference - 1000
          : timeDrift,
        driftAdded: !isFirstReplayAfterLoad ? timeDifference - 1000 : 0,
        replayIdentifier: overlay.detail.replayIdentifier,
        completionTime: new Date().toISOString(),
      });
    };

    window.addEventListener('replayOverlay', handleReplayOverlay);
    return () => {
      window.removeEventListener('replayOverlay', handleReplayOverlay);
    };
  }, [
    replayIdentifier,
    setReplayIdentifier,
    viewMode,
    setReplayOverlay,
    replayRequestDatetime,
    setReplayRequestDatetime,
    tracker,
  ]);

  return [replayOverlay, setReplayOverlay];
};
